/**
 * Reconstruct a device's templated RTSP suffix from the literal suffixes of its cameras.
 *
 * The legacy model stored one templated suffix per recorder (`/unicast/c{}/s1/live`); the camera
 * model stores one literal suffix per camera (`/unicast/c1/s1/live`, `/unicast/c2/s1/live`, …).
 * A discovery scan needs the template back, to probe slots that have no camera yet.
 *
 * The substitution cannot be done naively: `/unicast/c1/s1/live` contains the slot number twice,
 * so replacing every occurrence yields `/unicast/c{}/s{}/live` — a URL no DVR serves, which comes
 * back empty and reads as "this device has nothing". Instead, each occurrence is tried in turn and
 * kept only if it reproduces *every* camera's stored suffix.
 */

const SLOT_PLACEHOLDER = "{}";

/** Expand a template with a slot number, the way chauffeur's `.format(channel)` does. */
function expand(template, slot) {
  return template.split(SLOT_PLACEHOLDER).join(String(slot));
}

/**
 * One candidate template per occurrence of the slot number in the suffix.
 *
 * @param {string} suffix - A camera's literal suffix.
 * @param {number} slot - That camera's slot number.
 * @returns {string[]} The candidate templates, possibly empty.
 */
function candidatesFrom(suffix, slot) {
  const token = String(slot);
  const out = [];
  for (let at = suffix.indexOf(token); at !== -1; at = suffix.indexOf(token, at + 1)) {
    // A placeholder touching another digit is pad-ambiguous: on "/ch01/live" at slot 1 the token
    // matches inside the pad and yields "/ch0{}/live", which two single-digit cameras validate
    // happily and which then probes "/ch012/live" — nothing — for every slot above 9.
    const before = suffix[at - 1];
    const after = suffix[at + token.length];
    if (/\d/.test(before ?? "") || /\d/.test(after ?? "")) continue;
    out.push(suffix.slice(0, at) + SLOT_PLACEHOLDER + suffix.slice(at + token.length));
  }
  return out;
}

/**
 * Derive the device's suffix template, or `""` when it cannot be established with certainty.
 *
 * Returning `""` is deliberate for every ambiguous case — no camera, a single camera, cameras all
 * on one slot, or suffixes that agree on no single template. A guessed template silently probes
 * nothing; an empty field asks the operator, who knows the DVR.
 *
 * @param {Array<{slot_number: number|string, rtsp_url_suffix: string}>} cameras - The device's cameras.
 * @returns {string} The template carrying exactly one `{}`, or `""`.
 */
export default function deriveSuffixTemplate(cameras) {
  const usable = (cameras || [])
    .map((camera) => ({
      slot: Number(camera?.slot_number),
      suffix: String(camera?.rtsp_url_suffix ?? ""),
    }))
    .filter((camera) => Number.isInteger(camera.slot) && camera.slot >= 1 && camera.suffix !== "");

  // Two cameras on the same slot say nothing about which position varies.
  if (usable.length < 2 || new Set(usable.map((camera) => camera.slot)).size < 2) return "";

  // Seeded from every camera, not just the first: which one happens to sort first must not decide
  // whether a template is found. A candidate still has to reproduce *all* of them.
  const templates = usable
    .flatMap((camera) => candidatesFrom(camera.suffix, camera.slot))
    .filter(
      (template) =>
        template.split(SLOT_PLACEHOLDER).length - 1 === 1 &&
        usable.every((camera) => expand(template, camera.slot) === camera.suffix)
    );

  const distinct = [...new Set(templates)];
  return distinct.length === 1 ? distinct[0] : "";
}
